/**
 * Mount once at app root (inside <Provider>). Listens for cross-tab
 * subscription-state events and dispatches the right refetch thunks.
 *
 * Per design decision #13 / #14 and subscription-tiers-rewrite task 1.11.
 *
 * On `subscription_purchased`:
 *   - Dispatch `getUserMetadata` (tier/plan refresh).
 *   - If event.threadId is present AND matches the active thread, refetch
 *     thread/document metadata so bulk-unlocked tiles re-render.
 *
 * On `consultation_consumed`:
 *   - Dispatch `getUserMetadata` only (counter sync).
 *
 * On window focus (debounced ≥ METADATA_FOCUS_REFETCH_MIN_INTERVAL_MS):
 *   - Dispatch `getUserMetadata` (third safety net).
 */
import { useEffect, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";

import {
  subscribeToSubscriptionUpdates,
  subscribeToFocusEvents,
  METADATA_FOCUS_REFETCH_MIN_INTERVAL_MS,
  type CrossTabPayload,
} from "src/utils/crossTabSync";

// The onboarding slice is plain JS; we import via dynamic require to avoid a
// fully-typed boundary just for this glue component. Cast to a thunk-action
// creator shape at the call site.
type ThunkAction = (dispatch: unknown, getState: unknown) => unknown;
interface OnboardingActions {
  getUserMetadata: (arg?: unknown) => ThunkAction;
}

const getOnboardingActions = (): OnboardingActions => {
  // eslint-disable-next-line @typescript-eslint/no-var-requires
  const slice = require("src/store/slices/onboardingSlice") as OnboardingActions;
  return slice;
};

interface MinimalThreadState {
  threadData?: { id?: string | null } | null;
}

interface RootStateShape {
  messageSlice?: MinimalThreadState;
}

const CrossTabSyncBridge = (): null => {
  const dispatch = useDispatch();
  const activeThreadId = useSelector(
    (state: RootStateShape) => state.messageSlice?.threadData?.id ?? null
  );

  // Keep activeThreadId in a ref so the subscriber callbacks (mounted once)
  // always see the current value without re-subscribing on every thread change.
  const activeThreadIdRef = useRef<string | null>(activeThreadId);
  useEffect(() => {
    activeThreadIdRef.current = activeThreadId;
  }, [activeThreadId]);

  useEffect(() => {
    const { getUserMetadata } = getOnboardingActions();

    const onUpdate = (payload: CrossTabPayload) => {
      dispatch(getUserMetadata() as unknown as { type: string });

      if (
        payload.kind === "subscription_purchased" &&
        payload.threadId &&
        payload.threadId === activeThreadIdRef.current
      ) {
        // Refetch active-thread doc metadata so bulk-unlocked tiles re-render.
        // Mirrors the WS unlock pattern at WebsocketProvider.js:1306–1318.
        // eslint-disable-next-line @typescript-eslint/no-var-requires
        const { getAskThreadMessages } = require("src/store/slices/messageSlice");
        dispatch(
          getAskThreadMessages({
            threadId: payload.threadId,
            dispatchSource: "cross-tab-subscription-sync",
            force: true,
          }) as unknown as { type: string }
        );
      }
    };

    const onFocus = () => {
      dispatch(getUserMetadata() as unknown as { type: string });
    };

    const unsubUpdates = subscribeToSubscriptionUpdates(onUpdate);
    const unsubFocus = subscribeToFocusEvents(onFocus, {
      minIntervalMs: METADATA_FOCUS_REFETCH_MIN_INTERVAL_MS,
    });

    return () => {
      unsubUpdates();
      unsubFocus();
    };
    // dispatch is stable; subscribe once for the lifetime of this component.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return null;
};

export default CrossTabSyncBridge;
